/**
 * Service catalogue. `icon` values map to lucide-react icons via
 * components/ui/Icon.jsx, so data stays free of component imports.
 */

export const services = [
  {
    id: 'corporate-pickup',
    icon: 'Truck',
    title: 'Corporate E-Waste Pickup',
    summary:
      'Doorstep collection from offices, plants and warehouses with sealed transport and a chain-of-custody manifest at every handover.',
    deliverables: [
      'GPS-tracked, covered transport vehicles',
      'Sealed and numbered collection bins',
      'Weighbridge slip at facility intake',
      'Chain-of-custody manifest per consignment',
    ],
    highlight: 'Pan-India coverage across 50+ cities',
  },
  {
    id: 'data-destruction',
    icon: 'ShieldCheck',
    title: 'Data Destruction & Degaussing',
    summary:
      'NIST 800-88 compliant wiping, degaussing and physical shredding of drives, tapes and storage media — witnessed on site if you prefer.',
    deliverables: [
      'NIST 800-88 Purge & Destroy methods',
      'Serial-wise Certificate of Data Destruction',
      'Optional on-site shredding truck',
      'Video-recorded destruction on request',
    ],
    highlight: 'Serial-level audit trail for every drive',
  },
  {
    id: 'epr-credits',
    icon: 'Coins',
    title: 'EPR Credit Management',
    summary:
      'End-to-end handling of your Extended Producer Responsibility obligations — target planning, credit generation and portal reconciliation.',
    deliverables: [
      'EPR target assessment and planning',
      'Credit generation against your producer ID',
      'CPCB portal filing and reconciliation',
      'Quarterly obligation tracking dashboard',
    ],
    highlight: 'Zero pending entries across every consignment',
  },
  {
    id: 'form-6',
    icon: 'FileCheck2',
    title: 'Form 6 & Passbook Entry',
    summary:
      'Statutory documentation prepared, filed and reconciled for you — including Form 6 issuance and passbook entries against your account.',
    deliverables: [
      'Form 6 issuance for every consignment',
      'Passbook entry against your producer ID',
      'Annual return support (Form 9)',
      'Audit-ready document repository',
    ],
    highlight: 'Zero pending entries across every consignment',
  },
  {
    id: 'itad',
    icon: 'Laptop',
    title: 'IT Asset Disposition',
    summary:
      'Retire IT fleets with a value-recovery lens — grading, refurbishment and buy-back so obsolete assets return cash instead of costing storage.',
    deliverables: [
      'Asset audit, grading and valuation',
      'Transparent buy-back pricing',
      'Refurbish-or-recycle recommendation',
      'Settlement within 7 working days',
    ],
    highlight: 'Average 30% higher recovery than scrap rates',
  },
  {
    id: 'battery-recycling',
    icon: 'BatteryCharging',
    title: 'Battery & Li-ion Recycling',
    summary:
      'Safe collection, discharge and processing of lithium-ion, lead-acid and UPS battery banks under Battery Waste Management Rules, 2022.',
    deliverables: [
      'Thermal-safe packaging and transport',
      'Controlled discharge before processing',
      'Black-mass recovery reporting',
      'Battery Waste Rules 2022 documentation',
    ],
    highlight: 'Dedicated Li-ion handling bay and fire suppression',
  },
  {
    id: 'dismantling',
    icon: 'Wrench',
    title: 'On-site Dismantling',
    summary:
      'Server racks, DG sets, medical equipment and plant machinery dismantled at your premises by trained crews with full safety cover.',
    deliverables: [
      'Site survey and method statement',
      'Trained crew with PPE and insurance',
      'Segregation at source into waste streams',
      'Site handed back clean and photographed',
    ],
    highlight: 'Zero lost-time incidents',
  },
  {
    id: 'advisory',
    icon: 'ClipboardList',
    title: 'Compliance Advisory',
    summary:
      'A named compliance manager who keeps your obligations mapped, deadlines calendared and documentation inspection-ready year round.',
    deliverables: [
      'Obligation mapping and gap analysis',
      'Inspection and audit representation',
      'Policy drafting for internal ESG reporting',
      'Quarterly compliance health report',
    ],
    highlight: 'Named manager, 4-hour response SLA',
  },
];

/** Differentiators for the "Why Choose Us" section. */
export const whyChooseUs = [
  {
    icon: 'BadgeCheck',
    title: 'CPCB-Authorised Recyclers',
    description:
      'Get the best price for your e-waste from authorised recyclers.',
  },
  {
    icon: 'FileStack',
    title: 'Complete Documentation',
    description:
      'Form 6, passbook entries, recycling certificates, data destruction certificates and EPR credit notes — issued as standard, never as add-ons.',
  },
  {
    icon: 'Lock',
    title: 'Zero Data Exposure',
    description:
      'Sealed bins, vetted crews and NIST 800-88 destruction mean no device leaves your premises in a readable state.',
  },
  {
    icon: 'Clock',
    title: 'Predictable Turnaround',
    description:
      'Pickup within 48 working hours of verification, and compliance documentation delivered within 7 working days of processing.',
  },
  {
    icon: 'Recycle',
    title: '98.6% Material Recovery',
    description:
      'Mechanical separation and hydrometallurgical partners keep landfill diversion above 98% by weight across all streams.',
  },
  {
    icon: 'LineChart',
    title: 'ESG-Ready Reporting',
    description:
      'Every consignment converts into CO₂-avoided and material-recovered figures you can drop straight into your sustainability report.',
  },
];
